import React, { useState, useEffect, useRef, useMemo, useCallback, useId } from 'react';
import { ActivityIndicator } from 'react-native-paper';
import { View, Text, Pressable, Keyboard, ScrollView } from 'react-native';
import { format, sheetIcons, sheetTypeNames } from '@/utils/constants/sheet_formats';
import usePlatform from '@/utils/hooks/usePlatform';
import { Tooltip } from '@/components/General/Tooltip';
import SheetModal from '@/components/General/SheetModal';
import { useFilter } from '@/data/store/useFilterStore';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import SearchBox from '@/components/General/inputs/SearchBox';
import { LegendList } from "@legendapp/list";
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import ContentLoader from '@/components/General/ContentLoader';
import BackToTop from '@/components/General/BackToTop';
import { useQueryClient } from '@tanstack/react-query';
import {useEditScreenStore} from '@/data/store/useEditScreenStore';
import { useRouter } from 'expo-router';
import {StyleSheet} from 'react-native-unistyles';
import DropdownMenu from '../DropdownMenu';
import Unicon from '../Unicon';
import InlineHelpIcon from './InlineHelpIcon';
import Chip from '@/components/General/Chip';
import SolidButton from '@/components/General/SolidButton';
import PeopleAdvancedFilter from '@/components/Filters/PeopleAdvancedFilter';
import Avatar from '@/components/People/Avatar';
import SheetSelectorInfoModal from '@/components/General/inputs/SheetSelectorInfoModal';
import CutCard from '@/components/General/CutCard';
import { folderPickerRows, selectableOriginals } from '@/utils/folderPickerModel';

// Types that support the info modal in the selector list.
// Add new types here as their info content is implemented in SheetSelectorInfoModal.
const INFO_SUPPORTED_TYPES = new Set(['roles', 'roleFolders']);

// Types rendered as an expandable folder hierarchy instead of a flat list.
// 'roles' shows folder grouping with role rows inside; 'roleFolders' makes the
// folders themselves the selectable items (also used for form-folder pickers).
const FOLDER_TREE_TYPES = new Set(['roles', 'roleFolders']);

// Max selected-item chips shown in the trigger before collapsing to "+N more".
const MAX_VISIBLE_CHIPS = 9;

// Shared default for the excludeIds prop — a fresh `[]` per render would
// invalidate the folderPicker useMemo and rebuild the tree every render.
const EMPTY_IDS = [];

const SheetSelector = (props) => {
    // `allowTruncate`/`noDefaultTruncated` are deprecated no-ops: selected items now
    // always render as chips inside the trigger (wrapping, scrollable past 3 rows).
    // Callers still pass them; they are simply ignored (not destructured).
    const { title, titleOnly, value, tabIndex, required = false, errorFn = null, addScreenName = null, addScreenParams = null, placeholder, fromScreen = null, fromStoreKey = null, queryResult = null, queryResultSearch, excludeIds = EMPTY_IDS, recents = null, peopleSubset = null, subsetLabel = null, onChange, onOpen = () => {}, onClose = () => {}, onCancel, open, type = null, multiple = false, disabled = false, disableRemove = false, children, helpText = null, disableErroredOptions, forceModal = false, includeArchived = false, smallGroupTypeID = null, triggerTooltip = null, isNotOnSurface = false } = props;

    const searchBoxRef = useRef(null);

    const [ selectedItems, setSelectedItems ] = useState([])
    const [ showSelectedOnly, setShowSelectedOnly ] = useState(false)
    const [ selectedOriginalItems, setSelectedOriginalItems ] = useState(null)
    const [ searchText, setSearchText ] = useState("")
    const [ scrollAtTop, setScrollAtTop ] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [triggerLayout, setTriggerLayout] = useState(null);
    const [showArchived, setShowArchived] = useState(includeArchived);
    const [infoModalItemId, setInfoModalItemId] = useState(null);
    // Info-modal content type ('roles' role details vs 'roleFolders' folder
    // details) — folder rows inside a roles picker open the folder modal.
    const [infoModalItemType, setInfoModalItemType] = useState(null);
    // Folder-hierarchy state (roles/roleFolders only): expanded folder ids.
    // Everything starts collapsed (empty set).
    const [expandedFolderIDs, setExpandedFolderIDs] = useState(() => new Set());
    // People-subset mode (assignee pickers): when a caller supplies
    // `peopleSubset`, the list opens scoped to those people (recents pinned on
    // top) until the user opts into "Show everyone" for a one-off pick.
    const [showEveryone, setShowEveryone] = useState(false);
    const isFolderType = FOLDER_TREE_TYPES.has(type);
    const subsetAvailable = type === 'people' && (peopleSubset?.length || 0) > 0;
    const subsetActive = subsetAvailable && !showEveryone;
    // On web, the DropdownMenu (Radix) portals its content into the active modal
    // container (or document.body) and won't re-portal once mounted. So an info
    // SheetModal opened from a dropdown item renders BEHIND the dropdown. Fix:
    // close the dropdown while the info modal is open, then reopen it when info
    // closes. Native stacks correctly already (info modal is mounted inside the
    // sheet), so this flag is web-only.
    const reopenDropdownOnInfoCloseRef = useRef(false);

    const sheetFilterId = `sheet-selector-${useId()}`;
    const sheetFilterState = useFilter(sheetFilterId);

    const listRef = useRef();

    const router = useRouter();
    const { isWeb, isNative } = usePlatform();

    const {data: loggedInUser} = Query.people.loggedInUser()
    const setDataChangeFunction = useEditScreenStore(s => s.setDataChangeFunction)

    const queryClient = useQueryClient();
    // Always call hooks unconditionally to avoid hook ordering issues.
    // 'roles' uses Query.roleFolders.all (getRolesInFolders) instead of
    // Query.roles.all: it returns the same roles plus the folder tree
    // ({children, allRoles, allFolders}) needed for the hierarchical picker —
    // and it still applies the hidden-role visibility rules via `user`.
    // Enabled on mount (once loggedInUser is available for the visibility
    // rules), not on open: the shared ['roleFolders'] cache has staleTime
    // Infinity and backs every role/folder picker, so warming it here makes
    // the first open instant instead of a cold multi-second fetch. The
    // isModalOpen fallback preserves the old behavior when loggedInUser
    // never resolves.
    const internalQuery = (type == "roles" || type == "roleFolders")
        ? Query.roleFolders.all({user: loggedInUser, options: {enabled: !queryResult && (!!loggedInUser || isModalOpen)}}) 
        : (type == "people" 
            ? Query[type].all({searchTerm: searchText, filters: {...sheetFilterState.peopleFilters, ...(showArchived ? {includeArchived: 'true'} : {})}, filterLogic: sheetFilterState.filterLogic, options: {enabled: isModalOpen && !queryResult && !subsetActive}}) 
            : type == "smallGroups"
                ? Query[type].forDropdown({smallGroupTypeID, options: {enabled: isModalOpen && !queryResult}})
                : Query[type].all({options: {enabled: isModalOpen && !queryResult}}));
    
    // Use provided queryResult if available, otherwise use internal query
    const query = queryResult || internalQuery

    const isInfiniteQuery = type == "people";

    useEffect(() => {
        if(multiple){
            setSelectedItems(value?.filter(x => x) || []);
            
            if(!selectedOriginalItems){
                setSelectedOriginalItems(value?.filter(x => x) || []);
            }

            if(value?.length !== value?.filter(x => x)?.length){
                onChange(value?.filter(x => x))
            }
        }
    }, [value])

    // Stable string keys for memo deps (avoids JSON.stringify inside dep array).
    // query.data is used as a dep directly — React Query returns a new reference
    // whenever data changes, so serializing the whole (possibly paginated)
    // result set on every render is unnecessary.
    const filtersKey = JSON.stringify(sheetFilterState.peopleFilters || {});
    const recentsKey = JSON.stringify(recents || []);

    // Compute formatted data synchronously from query data + recents + search.
    // Using useMemo (not useEffect+setState) avoids race conditions where
    // searchText updates before query.data arrives from the server.
    const formattedData = useMemo(() => {
        // Folder-hierarchy types build their own row model below — skip the
        // flat format() pipeline entirely for them.
        if(isFolderType) return [];
        if(!subsetActive && !query.data) return [];

        // Subset mode sources the list from the caller-provided peopleSubset
        // (sorted by name) instead of the internal people query's pages.
        let allData = subsetActive
            ? [...peopleSubset].sort((a, b) => `${a?.firstName || ''} ${a?.lastName || ''}`.localeCompare(`${b?.firstName || ''} ${b?.lastName || ''}`))
            : type != "people" ? query.data : (query.data?.pages?.flatMap(page => page.data) || []);

        if(excludeIds?.length > 0){
            allData = allData.filter(item => !excludeIds.includes(item.id));
        }

        const applyErrors = (arr) => {
            if(!errorFn) return arr;
            return arr.map(d => {
                const errors = errorFn(d);
                return errors?.length > 0 ? {...d, errorMessages: errors} : d;
            });
        }

        const formattedNotFiltered = format(type, applyErrors(allData));

        // Prepend recents (pinned on top, deduped against the main list)
        if(recents?.length > 0){
            const recentIds = new Set();
            const recentFormatted = format(type, applyErrors(recents))
                .map(f => ({ ...f, isRecent: true }))
                .filter(f => {
                    if(recentIds.has(f.id)) return false;
                    recentIds.add(f.id);
                    return true;
                });

            // Remove recent items from the main list so they don't appear twice
            const withoutRecent = formattedNotFiltered.filter(item => !recentIds.has(item.id));
            const combined = [...recentFormatted, ...withoutRecent];

            // For people type (infinite query), the server handles search — don't
            // client-filter the main list (it's already server-filtered). Only filter
            // recents by search text. For other types (and subset mode, where the
            // subset is a small client-side list), filter everything client-side.
            const shouldClientFilter = searchText?.length > 0 && (type != "people" || !!queryResult || subsetActive);
            if(shouldClientFilter){
                const lower = searchText.toLowerCase();
                return combined.filter(item =>
                    item.heading?.toLowerCase().includes(lower) ||
                    item.sub_heading?.toLowerCase().includes(lower) ||
                    item.body?.toLowerCase().includes(lower)
                );
            }
            else if(searchText?.length > 0){
                // People type: only filter recents by search, keep server results as-is
                const lower = searchText.toLowerCase();
                const filteredRecents = recentFormatted.filter(item =>
                    item.heading?.toLowerCase().includes(lower) ||
                    item.sub_heading?.toLowerCase().includes(lower) ||
                    item.body?.toLowerCase().includes(lower)
                );
                return [...filteredRecents, ...withoutRecent];
            }
            return combined;
        }

        // No recents path
        if(searchText?.length > 0 && (type != "people" || !!queryResult || subsetActive)){
            const lower = searchText.toLowerCase();
            return formattedNotFiltered.filter(item =>
                item.heading?.toLowerCase().includes(lower) ||
                item.sub_heading?.toLowerCase().includes(lower) ||
                item.body?.toLowerCase().includes(lower)
            );
        }
        return formattedNotFiltered;
    }, [query.data, query.isFetching, searchText, filtersKey, showArchived, recentsKey, excludeIds, errorFn, type, queryResult, subsetActive, peopleSubset]);


    // Every open starts fully collapsed at the hierarchy root, regardless of
    // where the last session drilled. Search is cleared too — a stale
    // searchText would reopen into a filtered list, and queryResultSearch
    // keeps a caller-provided search in sync.
    const resetFolderState = useCallback(() => {
        setExpandedFolderIDs(new Set());
        setSearchText('');
        if (queryResultSearch && typeof queryResultSearch === 'function') queryResultSearch('');
    }, [queryResultSearch]);

    // Reset on every open. On web the DropdownMenu opens via its own
    // onOpenChange (openModal isn't called), so this must be an effect on
    // isModalOpen rather than inside openModal. A reopen triggered by the
    // info-modal round-trip consumes reopenDropdownOnInfoCloseRef instead —
    // the user's search text and expanded folders survive it.
    useEffect(() => {
        if (isModalOpen && isFolderType) {
            if (reopenDropdownOnInfoCloseRef.current) {
                reopenDropdownOnInfoCloseRef.current = false;
                return;
            }
            resetFolderState();
        }
    }, [isModalOpen, isFolderType, resetFolderState]);

    // Subset mode is the default view on every open — "Show everyone" is a
    // per-session opt-in for one-off picks, not a sticky preference.
    useEffect(() => {
        if (isModalOpen) setShowEveryone(false);
    }, [isModalOpen]);

    const openModal = () => {
        setIsModalOpen(true);
        if (isNative || forceModal) onOpen();
        // On web (DropdownMenu), focus after a short delay.
        // On native, SheetModal's onShow handles focusing after the modal animation completes.
        if (!isNative && !forceModal) {
            setTimeout(() => {
                searchBoxRef.current?.focus();
            }, 100);
        }
    }

    const closeModal = useCallback(() => {
        Keyboard.dismiss();
        setIsModalOpen(false);
        if (isNative || forceModal) onClose();
    }, [isNative, forceModal, onClose]);

    useEffect(() => {
        if (open === true && !isModalOpen) openModal();
        else if (open === false && isModalOpen) {
            closeModal();
            if (onCancel) onCancel();
        }
    }, [open])

    const formattedValue = useMemo(() => {
        if (!value || (!multiple && !value?.id)) return [];
        return multiple ? format(type, value?.filter(x => x)) : format(type, Array.isArray(value) ? value : [value]);
    }, [value, type, multiple]);

    // Shared search handler for the native SearchBox and web DropdownMenu
    // onSearch. For folder types, clearing search restores the fully collapsed
    // hierarchy (expanded set emptied).
    const handleSearchChange = useCallback((text) => {
        // Web info-modal round-trip: DropdownMenu fires onSearch("") on every
        // open/close — while the reopen flag is set that call must not wipe
        // the user's search text or folder state.
        if (reopenDropdownOnInfoCloseRef.current && !text?.trim()) return;
        setSearchText(text);
        if (isFolderType && !text?.trim()) {
            // resetFolderState clears searchText and notifies
            // queryResultSearch('') itself — nothing left to do here.
            resetFolderState();
            return;
        }
        if (queryResultSearch && typeof queryResultSearch === 'function') {
            queryResultSearch(text);
        }
    }, [isFolderType, queryResultSearch, resetFolderState]);

    const toggleFolderExpanded = useCallback((folderID) => {
        setExpandedFolderIDs(prev => {
            const next = new Set(prev);
            if (next.has(folderID)) next.delete(folderID);
            else next.add(folderID);
            return next;
        });
    }, []);

    const selectedIDs = useMemo(() => new Set(
        (multiple ? selectedItems : [value]).filter(Boolean).map(x => x.id)
    ), [selectedItems, value, multiple]);

    // Folder-hierarchy row model. Rows are {kind:'folder'|'role'} objects —
    // folder rows carry `folder`, `agg` (descendant-role totals), `isExpanded`,
    // `hasChildren`, `selectable`, `selectedDescendants`; role rows carry
    // `original`, `heading`, `sub_heading`, `body` (folder path in search),
    // `counts` {members, permissions, resources}, `isDynamic`, `isHidden`.
    const folderPicker = useMemo(() => {
        if (!isFolderType || !query.data) return { rows: [], model: null, isStructured: false, searching: false };
        return folderPickerRows({
            type,
            data: query.data,
            searchText,
            expandedIDs: expandedFolderIDs,
            selectedIDs,
            excludeIds,
            errorFn,
        });
    }, [isFolderType, type, query.data, searchText, expandedFolderIDs, selectedIDs, excludeIds, errorFn]);

    // Modal presentation (native sheet or forceModal on web) drops the rows'
    // horizontal gutter so the list runs edge-to-edge in the sheet.
    const modalMode = isNative || forceModal;

    // Toggles a multi-select item in-place (does not close) or commits a
    // single-select choice.
    const selectItem = useCallback((item) => {
        const entityID = item.original?.id;
        if (multiple) {
            let next;
            if (selectedIDs.has(entityID)) {
                if (disableRemove && item.isOriginal) return;
                next = selectedItems?.filter(x => x.id !== entityID);
            } else {
                next = [...(selectedItems || []), item.original];
            }
            setSelectedItems(next);
            // Web commits each pick immediately (matches the menu-options path);
            // native defers to the sheet's Confirm button.
            if (isWeb && !forceModal) onChange(next);
        } else {
            closeModal();
            onChange(item.original);
            Keyboard.dismiss();
        }
    }, [multiple, selectedIDs, selectedItems, disableRemove, onChange, isWeb, forceModal, closeModal]);

    // ─── Folder row (both tree modes) ───────────────────────────────────────
    // Compact indented row matching the Move Folder modal pattern: chevron
    // (or 26px spacer for leaves), fa5 folder icon, name, and a count Chip.
    // Selected rows get the primaryContainer fill + bold primary text
    // (modalFolderRowSelected).
    const FolderRow = ({item}) => {
        const folderID = item.folder.id;
        const hasErrors = item.original?.errorMessages?.length > 0;
        const rowDisabled = item.selectable && ((disableRemove && item.isOriginal) || (disableErroredOptions && hasErrors));

        // roles type: folders are organizational — row press expands/collapses.
        // roleFolders type: folders ARE the items — row press selects; the
        // sibling chevron toggles expansion (sibling, not nested, so presses
        // don't double-fire the row action). Search renders the same tree
        // (force-expanded), so row presses toggle/select normally there too.
        const handleRowPress = () => {
            if (item.selectable) selectItem(item);
            else if (item.hasChildren) toggleFolderExpanded(folderID);
        };

        const openInfo = () => {
            if (isWeb && !forceModal) {
                openInfoFromDropdown(folderID, 'roleFolders');
            } else {
                setInfoModalItemType('roleFolders');
                setInfoModalItemId(folderID);
            }
        };

        return <View style={[styles.treeRow(item.depth, modalMode), item.isSelected && styles.treeRowSelected, rowDisabled && {opacity: 0.5}]}>
            {/* Expand/collapse chevron — sibling of the row press so it never
                fires the row action. Childless folders get a fixed 26px spacer
                so names align across depths. */}
            {item.hasChildren ? (
                <Pressable
                    testID={`sheet-folder-toggle-${folderID}`}
                    accessibilityRole="button"
                    accessibilityLabel={item.isExpanded ? `Collapse ${item.heading}` : `Expand ${item.heading}`}
                    hitSlop={10}
                    style={styles.treeChevron}
                    onPress={() => toggleFolderExpanded(folderID)}
                >
                    <Unicon name={item.isExpanded ? "chevron-down" : "chevron-forward"} size={18} colorVariant="textSecondary" />
                </Pressable>
            ) : (
                <View style={styles.treeSpacer} />
            )}
            <Pressable testID={`sheet-folderitem-${type}-${folderID}`} style={styles.selectArea} disabled={rowDisabled} onPress={handleRowPress}>
                {multiple && item.selectable && (
                    <Unicon name={item.isSelected ? "checkbox" : "square-outline"} size={22} colorVariant={item.isSelected ? "success" : "textSecondary"} style={{marginRight: 8}}/>
                )}
                <Unicon library="fa5" name="folder" size={18} colorVariant={item.isSelected ? "primary" : "warning"} style={{marginRight: 8}} solid />
                <Text style={[styles.treeRowText, item.isSelected && styles.treeRowTextSelected]} numberOfLines={1}>{item.heading}</Text>
            </Pressable>
            <View style={styles.itemRightActions}>
                {item.selectedDescendants > 0 && (
                    <View style={styles.selectedBadge}><Text style={styles.selectedBadgeText}>{item.selectedDescendants} selected</Text></View>
                )}
                {folderPicker.isStructured && (
                    <Pressable
                        hitSlop={4}
                        onPress={openInfo}
                        style={styles.infoButtonWrapper}
                        testID={`sheet-folder-info-${type}-${folderID}`}
                    >
                        <Unicon name="information-circle-outline" size={24} colorVariant="info" />
                    </Pressable>
                )}
                {item.selectable && !multiple && (item.isSelected
                    ? <Unicon name="checkmark-circle" size={25} colorVariant="success"/>
                    : <Unicon name="chevron-forward-outline" size={25} colorVariant="text"/>
                )}
            </View>
        </View>
    };

    // ─── Role row (leaf of the folder tree) ─────────────────────────────────
    // Same compact row shape as folders: 26px spacer in place of the chevron,
    // no folder icon, role name + description/counts on the second line.
    const RoleRow = ({item}) => {
        const entityID = item.original?.id ?? item.id;
        const errorMessages = item?.original?.errorMessages?.map((m, i) => <Text key={i} style={styles.errorText}>{m}</Text>);
        const rowDisabled = (disableRemove && item.isOriginal) || (disableErroredOptions && item.original?.errorMessages?.length > 0);

        const openItemInfo = () => {
            if (isWeb && !forceModal) {
                openInfoFromDropdown(entityID, type);
            } else {
                setInfoModalItemType(type);
                setInfoModalItemId(entityID);
            }
        };

        return <View style={[styles.treeRow(item.depth, modalMode), item.isSelected && styles.treeRowSelected, rowDisabled && {opacity: 0.5}]}>
            <View style={styles.treeSpacer} />
            <Pressable testID={`sheet-item-${type}-${entityID}`} style={styles.selectArea} disabled={rowDisabled} onPress={() => selectItem(item)}>
                {multiple && (
                    <Unicon name={item.isSelected ? "checkbox" : "square-outline"} size={22} colorVariant={item.isSelected ? "success" : "textSecondary"} style={{marginRight: 8}}/>
                )}
                <View style={{flex: 1}}>
                    <View style={styles.headingRow}>
                        <Text style={[styles.treeRowText, item.isSelected && styles.treeRowTextSelected]} numberOfLines={1}>{item.heading}</Text>
                        {item.isHidden && <Unicon name="eye-off-outline" size={14} colorVariant="warning" />}
                    </View>
                    { !!item.sub_heading && <Text numberOfLines={2} style={styles.sub_header}>{item.sub_heading}</Text> }
                    { !!item.body && <Text numberOfLines={1} style={styles.body}>{item.body}</Text> }
                    {item.counts && (
                        <View style={styles.countsRow}>
                            {item.isDynamic
                                ? <View style={styles.countPill}><Unicon name="sync-outline" size={13} colorVariant="info" /><Text style={styles.countPillTextInfo}>Dynamic</Text></View>
                                : <CountPill icon="people-outline" value={item.counts.members} />
                            }
                            <CountPill icon="key-outline" value={item.counts.permissions} />
                            <CountPill icon="layers-outline" value={item.counts.resources} />
                        </View>
                    )}
                    { errorMessages }
                </View>
            </Pressable>
            <View style={styles.itemRightActions}>
                {INFO_SUPPORTED_TYPES.has(type) && (
                    <Pressable
                        hitSlop={4}
                        onPress={openItemInfo}
                        style={styles.infoButtonWrapper}
                        testID={`sheet-item-info-${type}-${entityID}`}
                    >
                        <Unicon name="information-circle-outline" size={24} colorVariant="info" />
                    </Pressable>
                )}
                {!multiple && (item.isSelected
                    ? <Unicon name="checkmark-circle" size={25} colorVariant="success"/>
                    : <Unicon name="chevron-forward-outline" size={25} colorVariant="text"/>
                )}
            </View>
        </View>
    };

    // Small icon+number inline count (members / permissions / resources).
    const CountPill = ({icon, value}) => (
        <View style={styles.countPill}>
            <Unicon name={icon} size={13} colorVariant="textSecondary" />
            <Text style={styles.countPillText}>{value}</Text>
        </View>
    );

    const RenderItem = ({item}) => {
        if (item.kind === 'folder') return <FolderRow item={item} />;
        if (item.kind === 'role') return <RoleRow item={item} />;
        const entityID = item.original?.id ?? item.id;
        const errorMessages = item?.original?.errorMessages?.map((m, i) => <Text key={i} style={styles.errorText}>{m}</Text>)
        const rowDisabled = (disableRemove && item.isOriginal) || (disableErroredOptions && item.original?.errorMessages?.length > 0);

        const openItemInfo = () => {
            if (isWeb && !forceModal) {
                openInfoFromDropdown(entityID, type);
            } else {
                setInfoModalItemType(type);
                setInfoModalItemId(entityID);
            }
        };

        // The info button is rendered as a SIBLING of the selecting Pressable
        // (not nested inside it). Nested Pressables don't reliably suppress the
        // parent's onPress on all platforms, so tapping info would also select
        // the item. Splitting them guarantees the info tap only opens the info
        // modal and leaves the sheet open.
        return <CutCard
            key={"sheet_" + title + "_" + item.id}
            style={styles.card(item.isSelected, rowDisabled)}
            contentStyle={styles.cardContent}
        >
            <Pressable testID={`sheet-item-${type}-${entityID}`} style={styles.selectArea} disabled={rowDisabled} onPress={() => selectItem(item)}>
                {multiple && (
                    <View style={styles.checkboxLeft}>
                        <Unicon name={item.isSelected ? "checkbox" : "square-outline"} size={25} colorVariant={item.isSelected ? "success" : "textSecondary"}/>
                    </View>
                )}
                {type === "people" && (
                    <View style={styles.avatarWrap}>
                        <Avatar size={34} profile={item.original} disableBorder onPress={() => {}} />
                    </View>
                )}
                <View style={{flex:1}}>
                    <View style={styles.headingRow}>
                        <Text style={styles.header(Math.max(triggerLayout?.width, 300))}>{item.heading}</Text>
                        {item.isHidden && <Unicon name="eye-off-outline" size={14} colorVariant="warning" />}
                    </View>
                    { !!item.sub_heading && <Text numberOfLines={2} style={styles.sub_header}>{item.sub_heading}</Text> }
                    { !!item.body && <Text numberOfLines={1} style={styles.body}>{item.body}</Text> }
                    {item.kind === 'role' && item.counts && (
                        <View style={styles.countsRow}>
                            {item.isDynamic
                                ? <View style={styles.countPill}><Unicon name="sync-outline" size={13} colorVariant="info" /><Text style={styles.countPillTextInfo}>Dynamic</Text></View>
                                : <CountPill icon="people-outline" value={item.counts.members} />
                            }
                            <CountPill icon="key-outline" value={item.counts.permissions} />
                            <CountPill icon="layers-outline" value={item.counts.resources} />
                        </View>
                    )}
                    { errorMessages }
                </View>
            </Pressable>
            <View style={styles.itemRightActions}>
                {item.isRecent && <View style={styles.recentBadge}><Text style={styles.recentBadgeText}>Recent</Text></View>}
                {INFO_SUPPORTED_TYPES.has(type) && (
                    <Pressable
                        hitSlop={4}
                        onPress={openItemInfo}
                        style={styles.infoButtonWrapper}
                        testID={`sheet-item-info-${type}-${entityID}`}
                    >
                        <Unicon name="information-circle-outline" size={24} colorVariant="info" />
                    </Pressable>
                )}
                {!multiple && (item.isSelected
                    ? <Unicon name="checkmark-circle" size={25} colorVariant="success"/>
                    : <Unicon name="chevron-forward-outline" size={25} colorVariant="text"/>
                )}
            </View>
        </CutCard>
    }

    const filtered_data = useMemo(() => {
        if (isFolderType) {
            // "Show Selected" collapses the tree to a flat selected list.
            if (showSelectedOnly) {
                return format(type, selectedItems).map(f => ({...f, kind: 'item', isSelected: true, isOriginal: selectedOriginalItems?.some(x => x.id === f.id)}));
            }
            return folderPicker.rows.map(r => ({...r, isOriginal: selectedOriginalItems?.some(x => x.id === r.original?.id)}));
        }
        return (showSelectedOnly 
            ? format(type, selectedItems).map(f => ({...f, isSelected: true, isOriginal: selectedOriginalItems?.some(x => x.id === f.id)}))
            : formattedData.map(f => ({
                ...f, 
                isSelected: multiple ? selectedItems?.some(x => x.id === f.id) : formattedValue?.some(x => x.id === f.id), 
                isOriginal: selectedOriginalItems?.some(x => x.id === f.id)
            }))
        );
    }, [isFolderType, folderPicker.rows, formattedData, selectedItems, showSelectedOnly, multiple, formattedValue, selectedOriginalItems, type])

    // Select-All (native actions menu + web toolbar share this): commit every
    // selectable entity in the whole tree — roles for 'roles', folders for
    // 'roleFolders' — NOT just the rendered rows, so collapsed folders'
    // contents are included. During search the rendered (filtered) rows are
    // the scope. Web commits immediately (no Confirm step); native defers to
    // Confirm.
    const selectAll = useCallback(() => {
        const next = isFolderType
            ? (folderPicker.searching
                ? folderPicker.rows
                    .filter(r => r.kind === (type === 'roleFolders' ? 'folder' : 'role'))
                    .map(r => r.original)
                : selectableOriginals({
                    model: folderPicker.model,
                    type,
                    excludeSet: new Set(excludeIds),
                }))
            : filtered_data.map(item => item.original);
        setSelectedItems(next);
        if (isWeb && !forceModal) onChange(next);
    }, [isFolderType, folderPicker, type, excludeIds, filtered_data, isWeb, forceModal, onChange]);

    // Selected items rendered as removable chips INSIDE the trigger input.
    // Original items are not removable when disableRemove is set (matches list-row behavior).
    // Max 9 chips shown; overflow collapses into a "+N more" chip that opens the picker.
    const selectedChips = useMemo(() => {
        if (!multiple || !value) return null;
        const chips = formattedValue?.slice(0, MAX_VISIBLE_CHIPS).map((pillItem, index) => {
            const isOriginal = selectedOriginalItems?.some(x => x.id === pillItem.id);
            const canRemove = !disabled && !(disableRemove && isOriginal);
            return <Chip
                key={`chip-${pillItem?.id}-${index}`}
                colorVariant="primary"
                onRemove={canRemove ? () => {
                    const newValue = selectedItems?.filter(x => x.id !== pillItem.id)
                    setSelectedItems(newValue);
                    onChange(newValue)
                } : undefined}
                removeTestID={`sheet-chip-remove-${pillItem.id}`}
            >
                {pillItem?.heading}
            </Chip>
        }) || [];

        const overflowCount = (formattedValue?.length || 0) - MAX_VISIBLE_CHIPS;
        if (overflowCount > 0) {
            // On web the press bubbles to the Zeego trigger and opens the dropdown;
            // on native the child Pressable wins the responder, so call openModal directly.
            chips.push(
                <Pressable
                    key="chip-overflow"
                    testID="sheet-chip-overflow"
                    hitSlop={4}
                    onPress={() => { if (!isWeb) openModal(); }}
                >
                    <Chip colorVariant="textSecondary">{`+${overflowCount} more`}</Chip>
                </Pressable>
            );
        }
        return chips;
    }, [multiple, value, formattedValue, selectedItems, selectedOriginalItems, disabled, disableRemove])

    const refreshList = useCallback(() => {
        if (isFolderType) {
            // roleFolders.all backs the internal hierarchy query; roles.all any
            // other consumers of the flat roles list; formFolders.all covers
            // 'roleFolders' pickers fed a caller-provided queryResult (form
            // folder pickers) where the internal query never runs.
            queryClient.invalidateQueries({queryKey: QueryKeys.roleFolders.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.roles.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.formFolders.all()});
            return;
        }
        queryClient.invalidateQueries({queryKey: QueryKeys[type].all()})
        queryClient.invalidateQueries({queryKey: QueryKeys[type]?.all({searchTerm: null})})
        // Subset rows come from the caller's query (team members), not the
        // internal people query — refresh that source too.
        if(peopleSubset?.length > 0){
            queryClient.invalidateQueries({queryKey: QueryKeys.taskTeams.all()});
        }
    }, [type, isFolderType, peopleSubset])

    // Web only: open the info modal from a dropdown item, closing the dropdown
    // first so the info modal stacks on top. The dropdown is reopened when the
    // info modal closes (see closeInfoModal). `infoType` selects role vs folder
    // content ('roles' pickers contain folder rows too).
    const openInfoFromDropdown = useCallback((id, infoType = type) => {
        reopenDropdownOnInfoCloseRef.current = true;
        setInfoModalItemType(infoType);
        setIsModalOpen(false);
        setInfoModalItemId(id);
    }, [type]);

    const closeInfoModal = useCallback(() => {
        setInfoModalItemId(null);
        if (reopenDropdownOnInfoCloseRef.current) {
            // Leave the ref set — the isModalOpen effect consumes it to skip
            // the open-time folder-state reset, and handleSearchChange uses it
            // to ignore DropdownMenu's reopen onSearch("").
            setIsModalOpen(true);
        }
    }, []);

    const goToAddScreen = () => {
        setDataChangeFunction(onChange, multiple ? (selectedItems || []) : undefined)
        router.push({pathname: addScreenName, params: {fromScreen, fromStoreKey, ...(addScreenParams || {})}})
        closeModal();
        Keyboard.dismiss();
    }

    // Actions menu for the sheet header. Uses the shared DropdownMenu (which handles
    // web portal/z-index and styling) instead of raw Zeego. Toggle items (Show Selected,
    // Show Archived) use a checkmark icon to indicate state since DropdownMenu's
    // multiple-mode checkboxes apply to ALL items, not a subset.
    // Singular entity name for create actions — `title` is plural/purpose-built
    // ("Assignees") so "Add Assignees" reads wrong; fall back to it when the
    // type has no mapped name.
    const typeLabel = sheetTypeNames[type] || title;

    const actionMenuOptions = [
        addScreenName != null && {
            id: "add",
            name: `Create New ${typeLabel}`,
            icon: { android: "add", ios: "plus", web: "add-circle-outline" },
            onSelect: goToAddScreen,
        },
        multiple && (type != "people" || (type == "people" && filtered_data?.length < 50 && !showSelectedOnly)) && {
            id: "select-all",
            name: "Select All",
            icon: { android: "check", ios: "checkmark.circle", web: "checkbox-outline" },
            // Folder pickers: every selectable entity in scope (roles for
            // 'roles', folders for 'roleFolders') — including rows hidden
            // inside collapsed folders, which rendered-row filtering missed.
            onSelect: selectAll,
        },
        (selectedItems?.length > 0 || value) && {
            id: "clear",
            name: "Clear Selections",
            icon: { android: "delete", ios: "trash", web: "close-circle-outline" },
            onSelect: () => {
                setSelectedItems([]);
                onChange(multiple ? [] : undefined);
            },
        },
        {
            id: "refresh",
            name: "Refresh List",
            icon: { android: "refresh", ios: "arrow.clockwise", web: "refresh-outline" },
            onSelect: refreshList,
        },
        { separator: true },
        {
            id: "show-selected",
            name: "Show Selected",
            icon: showSelectedOnly
                ? { android: "check_box", ios: "checkmark.square.fill", web: "checkbox" }
                : { android: "check_box_outline_blank", ios: "square", web: "square-outline" },
            selected: showSelectedOnly,
            onSelect: () => setShowSelectedOnly(prev => !prev),
        },
        type === "people" && !subsetActive && {
            id: "show-archived",
            name: "Show Archived",
            icon: showArchived
                ? { android: "check_box", ios: "checkmark.square.fill", web: "checkbox" }
                : { android: "check_box_outline_blank", ios: "square", web: "square-outline" },
            selected: showArchived,
            onSelect: () => setShowArchived(prev => !prev),
        },
    ].filter(Boolean);

    const actionsMenu = (
        <DropdownMenu
            testID={`sheet-actions-${title}`}
            options={actionMenuOptions}
            align="end"
            triggerComponent={
                <SolidButton
                    size="sm"
                    variant="muted"
                    type="textSecondary"
                    text="Actions"
                    iconName="chevron-down"
                />
            }
        />
    );

    // ─── Web dropdown content for folder-tree types ─────────────────────────
    // Roles/roleFolders can't render a nested folder tree as flat Zeego menu
    // items (no indentation, expand state, or drill-in affordances), so the
    // DropdownMenu swaps its whole inner content for this panel. Row presses
    // are plain Pressables inside the Radix layer — they never emit
    // ITEM_SELECT, so multiselect picks keep the menu open for free and a
    // single outside/trigger click still closes it once.
    const webActionButton = ({icon, library, tooltip, onPress, testID, visible = true}) => visible ? (
        <Tooltip key={testID} title={tooltip}>
            <Pressable testID={testID} accessibilityRole="button" accessibilityLabel={tooltip} onPress={onPress} style={styles.pickerToolbarButton} hitSlop={4}>
                <Unicon name={icon} library={library} size={19} colorVariant="text" />
            </Pressable>
        </Tooltip>
    ) : null;

    const webPickerContent = (closeDropdown) => (
        <View style={styles.pickerContent}>
            <View style={styles.pickerToolbar}>
                <Text style={styles.pickerToolbarTitle} numberOfLines={1}>{title || placeholder}</Text>
                <View style={styles.pickerToolbarActions}>
                    {webActionButton({icon: 'add-circle-outline', tooltip: `Create New ${typeLabel}`, testID: `SheetPicker-create-${type}`, visible: addScreenName != null, onPress: () => goToAddScreen()})}
                    {webActionButton({icon: 'checkbox-outline', tooltip: 'Select all', testID: `SheetPicker-selectall-${type}`, visible: multiple && !showSelectedOnly, onPress: selectAll})}
                    {webActionButton({icon: showSelectedOnly ? 'checkbox' : 'square-outline', tooltip: showSelectedOnly ? 'Show all options' : 'Show only selected options', testID: `SheetPicker-showselected-${type}`, visible: multiple && selectedItems?.length > 0, onPress: () => setShowSelectedOnly(prev => !prev)})}
                    {webActionButton({icon: 'refresh-outline', tooltip: 'Refresh list', testID: `SheetPicker-refresh-${type}`, onPress: () => refreshList()})}
                    {webActionButton({icon: 'delete-sweep', library: 'material', tooltip: 'Clear selection(s)', testID: `SheetPicker-clear-${type}`, visible: selectedItems?.length > 0 || !!value, onPress: () => {
                        setSelectedItems([]);
                        onChange(multiple ? [] : undefined);
                        closeDropdown();
                    }})}
                    <View style={styles.pickerToolbarSep} />
                    {webActionButton({icon: 'close', tooltip: 'Close', testID: `SheetPicker-close-${type}`, onPress: () => closeDropdown()})}
                </View>
            </View>

            <View style={styles.pickerSearchRow}>
                {/* testID → "SearchBox-sheet-picker-<type>" for E2E selectors. */}
                <View style={styles.searchBoxWrap}>
                    <SearchBox ref={searchBoxRef} nobackground testID={`sheet-picker-${type}`} searchText={searchText} onChangeText={handleSearchChange} />
                </View>
                {multiple && <Text style={styles.selectionCount}>{selectedItems?.length || 0} selected</Text>}
            </View>

            <ScrollView style={styles.pickerList} contentContainerStyle={styles.pickerListContent} keyboardShouldPersistTaps="handled">
                {query.isFetching && !query.isFetchingNextPage
                    ? <View style={styles.loaderContainer}><ActivityIndicator size="small" /></View>
                    : filtered_data.length === 0
                        ? <EmptyListPlaceholder message="Well... Looks like this list is empty." />
                        : filtered_data.map(item => <RenderItem key={item.id} item={item} />)
                }
            </ScrollView>

            {!query.isFetching && filtered_data.length > 0 && (
                <Text style={styles.pickerFooter}>
                    {filtered_data.length === 1 ? 'Showing the only result' : filtered_data.length === 2 ? 'Showing both results' : `Showing all ${filtered_data.length} results`}
                </Text>
            )}
        </View>
    );

    const shouldGiveExtraPadding = value && (!multiple || value.length > 0) && !titleOnly && ( (!multiple && (formattedValue[0]?.sub_heading || formattedValue[0]?.body)));
    const hasChips = multiple && value?.length > 0;

    const inputBox = (
        <Pressable
            hitSlop={10}
            testID={`SheetModalInput-${title}`}
            onLayout={!isWeb ? undefined : (e) => setTriggerLayout(e.nativeEvent.layout)}
            disabled={isWeb}
            tabIndex={tabIndex}
            style={styles.container(titleOnly, shouldGiveExtraPadding, hasChips, disabled, isNotOnSurface)}
            onPress={() => {
                openModal()
            }}
        >
            {formattedValue.length === 0
                ? <View style={styles.inputContent}>
                    <Unicon name={sheetIcons[type]} size={18} colorVariant="textSecondary"/>
                    <View style={styles.placeholderText(isWeb)}><Text style={styles.placeholder}>{placeholder || (multiple ? "Select Options" : "Select an Option")}</Text></View>
                </View>
                : (multiple
                    ? <View style={styles.chipsWrap}>
                        {selectedChips}
                    </View>
                    : <View>
                        <Text style={styles.header(Math.max(triggerLayout?.width, 300))}>{formattedValue[0]?.heading}</Text>
                        { formattedValue[0]?.sub_heading && !titleOnly ? <Text style={styles.sub_header}>{formattedValue[0]?.sub_heading}</Text> : <View></View> }
                        { formattedValue[0]?.body && !titleOnly ? <Text style={styles.body}>{formattedValue[0]?.body}</Text> : <View></View> }
                    </View>
                )
            }

            { !disabled && 
                <View style={{marginHorizontal: 5, marginRight: 10}}>
                    <Unicon name="chevron-expand-outline" colorVariant="textSecondary" size={18}/>
                </View> 
            }
        </Pressable>
    )

    return(
        <View style={styles.main(disabled)}>
            <View>

                {!children && title &&
                    <View style={styles.labelContainer}>
                        <Text style={styles.labelText}>
                            {title}
                            {required && <Text style={styles.required}>*</Text>}
                            {helpText && <InlineHelpIcon text={helpText} />}
                        </Text>
                    </View>
                }

                <View style={styles.inputWrapper(!children)}>
                    {(isWeb && !forceModal) 
                        ? <DropdownMenu
                            testID={`SheetModalInput-${title}`}
                            disabled={disabled}
                            showFocusOutline
                            shouldStopPropagation
                            title={title || placeholder}
                            // Folder-tree types render the hierarchical picker
                            // panel instead of flat menu options.
                            dropdownComponent={isFolderType ? (close) => webPickerContent(close) : undefined}
                            options={isFolderType ? [] : filtered_data?.map(item => ({
                                id: item.id,
                                icon: type === "people" ? {avatar: <Avatar size={26} profile={item.original} disableBorder onPress={() => {}}/>}: undefined,
                                name: item.heading,
                                subtitle: item.sub_heading,
                                disabled: disableErroredOptions && item.original?.errorMessages?.length > 0,
                                body: (item.original?.errorMessages?.length > 0 ? item.original?.errorMessages?.join(', ') : item.body),
                                isError: item.original?.errorMessages?.length > 0,
                                selected: item.isSelected,
                                infoAction: INFO_SUPPORTED_TYPES.has(type) ? () => openInfoFromDropdown(item.id) : undefined,
                                onSelect: () => {
                                    if(multiple){
                                        const currentSelectedItems = [...selectedItems]
                                        if(item.isSelected){
                                            if(disableRemove && item.isOriginal) return false
                                        
                                            const index = currentSelectedItems?.findIndex(x => x.id === item.id)
                                            if(index > -1) currentSelectedItems?.splice(index, 1)
                                        }
                                        else{
                                            currentSelectedItems?.push(item.original)
                                        }

                                        onChange(currentSelectedItems)
                                        setSelectedItems(currentSelectedItems)
                                    }
                                    else{
                                        onChange(item.original)
                                    }
                                }
                            }))}
                            showSearch={!isFolderType}
                            multiple={multiple}
                            dropdownWidth={Math.max(triggerLayout?.width, isFolderType ? 360 : 300)}
                            triggerComponent={children ? (triggerTooltip
                                ? <Tooltip title={triggerTooltip} hitSlop={10}>{children}</Tooltip>
                                : <Pressable hitSlop={10} onPress={() => {
                                    openModal()
                                }}>{children}</Pressable>) : inputBox}
                            isLoading={query.isFetching}
                            onSearch={handleSearchChange}
                            toolBarButtons={isFolderType ? [] : [
                                { icon:{name:"add", size:20}, name: "Create", visible: addScreenName != null, tooltip: `Create New ${typeLabel}`, onPress:() => goToAddScreen() },
                                { icon:{name: showEveryone ? "people" : "people-outline", size:20}, name: "SubsetToggle", visible: subsetAvailable, tooltip: showEveryone ? `Show ${subsetLabel || 'team'} members` : 'Show everyone', onPress:() => setShowEveryone(prev => !prev), keepOpen: true },
                                { icon:{name: showArchived ? "archive-off" : "archive-eye", size:20, library:"material"}, name: showArchived ? "Hide Archived" : "Show Archived", visible: type === "people", tooltip: showArchived ? "Hide archived people" : "Show archived people", onPress:() => setShowArchived(prev => !prev), keepOpen: true },
                                { icon:{name: showSelectedOnly ? "checkbox" : "square-outline", size:20}, name: showSelectedOnly ? "Show All" : "Show Selected", visible: multiple && selectedItems?.length > 0, tooltip: showSelectedOnly ? "Show all options" : "Show only selected options", onPress:() => setShowSelectedOnly(prev => !prev), keepOpen: true },
                                { icon:{name:"refresh-outline", size:20}, name: "Refresh", visible: true, tooltip: "Refresh list", onPress:() => refreshList(), keepOpen: true },
                                { icon:{name:"delete-sweep", library:"material", size:20}, name: "Clear", visible: selectedItems?.length > 0 || value, tooltip: "Clear selection(s)", onPress:() => {
                                    setSelectedItems([]);
                                    onChange(multiple ? [] : undefined)
                                    closeModal();
                                } }
                            ]}
                            open={isModalOpen}
                            onOpenChange={(willOpen) => setIsModalOpen(willOpen)}
                            onOpen={onOpen}
                            onClose={onClose}
                        /> 
                        : children ? (triggerTooltip
                            ? <Tooltip title={triggerTooltip} hitSlop={10} style={{minHeight: 44, justifyContent: "center"}} onPress={() => {
                                openModal()
                            }}>{children}</Tooltip>
                            : <Pressable hitSlop={10} style={{minHeight: 44, justifyContent: "center"}} onPress={() => {
                                openModal()
                            }}>{children}</Pressable>) : inputBox
                    }
                </View>

            </View>

            <SheetModal title={title || placeholder} isOpen={(isNative || forceModal) && isModalOpen} onClose={closeModal} onShow={() => searchBoxRef.current?.focus()} snapPoints={["90%"]} confirmText={multiple && selectedItems?.length > 0 ? `Confirm (${selectedItems.length})` : 'Confirm'} onConfirm={multiple ? () => {
                    onChange(selectedItems)
                    closeModal();
                    Keyboard.dismiss();
                } : null} 
            >
                <View style={styles.sheetHeader}>
                    <View style={styles.searchRow}>
                        <View style={styles.searchBoxContainer}>
                            <SearchBox ref={searchBoxRef} nobackground searchText={searchText} onChangeText={handleSearchChange}/>
                        </View>
                        {type == "people" && !subsetActive && (
                            <View style={styles.headerActions}>
                                <PeopleAdvancedFilter filterId={sheetFilterId} />
                            </View>
                        )}
                    </View>

                    {subsetAvailable && (
                        <Pressable
                            testID="SheetSelector-show-everyone"
                            accessibilityRole="button"
                            accessibilityLabel={showEveryone ? `Show ${subsetLabel || 'team'} members` : 'Show everyone'}
                            style={styles.subsetRow}
                            onPress={() => setShowEveryone(prev => !prev)}
                        >
                            <Unicon name="people-outline" size={15} colorVariant="tasks" />
                            <Text style={styles.subsetRowText} numberOfLines={1}>
                                {showEveryone ? 'Showing everyone' : `Showing ${subsetLabel ? `${subsetLabel} ` : ''}members`}
                            </Text>
                            <Text style={styles.subsetRowAction}>{showEveryone ? `Show ${subsetLabel || 'team'} members` : 'Show everyone'}</Text>
                        </Pressable>
                    )}

                    {/* Selection count left, Actions menu right. In single mode the
                        count is omitted and the menu stays right-aligned. */}
                    <View style={styles.actionsRow}>
                        {multiple
                            ? <Text style={styles.selectionCount}>{selectedItems?.length || 0} selected</Text>
                            : <View />
                        }
                        {actionsMenu}
                    </View>
                </View>
            
                <View style={styles.listContainer(isWeb)}>
                    <LegendList 
                        recycleItems 
                        keyExtractor={(item) => item.id} 
                        showsVerticalScrollIndicator={false}
                        ref={listRef}
                        scrollEventThrottle={100} 
                        keyboardShouldPersistTaps="handled"
                        onScroll={(e) => {
                            if(!scrollAtTop && e.nativeEvent.contentOffset.y == 0){
                                setScrollAtTop(true)
                            }
                            else if(scrollAtTop && e.nativeEvent.contentOffset.y > 0){
                                setScrollAtTop(false)
                            }
                        }}
                            estimatedItemSize={80}
                        data={(query.isFetching && !query.isFetchingNextPage) ? [] : filtered_data}
                        progressViewOffset={1000}
                        refreshing={false}
                        onRefresh={refreshList}
                        renderItem={RenderItem}
                        onEndReached={() => (isInfiniteQuery && !query.isFetching && query.hasNextPage) && query.fetchNextPage()}
                        contentContainerStyle={{paddingBottom:50}}
                        ListFooterComponent={!query.isFetching && !query.isFetchingNextPage
                            ? <View style={styles.listFooter}>{(isInfiniteQuery && query.hasNextPage && !showSelectedOnly) 
                                ? <ContentLoader containerHeight={70} /> 
                                :   (
                                        filtered_data?.length == 0 ? <EmptyListPlaceholder message="Well... Looks like this list is empty." /> 
                                        : filtered_data?.length == 1 ? <Text style={styles.centered}>Showing the only result</Text>
                                        : filtered_data?.length == 2 ? <Text style={styles.centered}>Showing both results</Text>
                                        : <Text style={styles.centered}>Showing all {filtered_data?.length} results</Text>
                                    )
                                }
                                </View> 
                            : <View style={styles.loaderContainer}><ActivityIndicator size="small" /></View>}
/>
                </View>

                <BackToTop ref={listRef} hidden={scrollAtTop} />

                {/* On native, a RN Modal (SheetSelectorInfoModal) only presents above the
                    currently presented sheet Modal if it is mounted INSIDE it — a sibling
                    Modal mounts in the main window underneath the pageSheet and never appears
                    (same pattern as PeopleAdvancedFilter above). On web the DropdownMenu path
                    is used and this SheetModal is never visible (Modal children don't mount
                    when visible=false), so the info modal must live outside instead. */}
                {INFO_SUPPORTED_TYPES.has(type) && (isNative || forceModal) && (
                    <SheetSelectorInfoModal
                        type={infoModalItemType || type}
                        itemId={infoModalItemId}
                        isOpen={!!infoModalItemId}
                        onClose={() => setInfoModalItemId(null)}
                    />
                )}
            </SheetModal>

            {INFO_SUPPORTED_TYPES.has(type) && !isNative && !forceModal && (
                <SheetSelectorInfoModal
                    type={infoModalItemType || type}
                    itemId={infoModalItemId}
                    isOpen={!!infoModalItemId}
                    onClose={closeInfoModal}
                />
            )}
        </View>
    )
}

const styles = StyleSheet.create(({colors, fonts, shadows, spacing, roundness, typography}, rt) => ({
    main: (disabled) => ({
        opacity: disabled ? 0.8 : 1, 
        pointerEvents: disabled ? "none" : "auto",
        // marginVertical: spacing.sm,
    }),
    header: (width) => ({
        fontSize: typography.body,
        textAlign: "left",
        fontFamily: fonts.body,
        width: width - 130,
        flexShrink: 1,
        color: colors.text,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }),
    itemRightActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    checkboxLeft: {
        alignItems: "center",
        justifyContent: "center",
        marginRight: 12,
    },
    avatarWrap: {
        marginRight: 12,
    },
    infoButtonWrapper: {
        width: 32,
        height: 32,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        _web: {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    recentBadge: {
        backgroundColor: colors.primaryContainer,
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
    recentBadgeText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    container: (titleOnly, shouldGiveExtraPadding, hasChips, disabled, isNotOnSurface) => ({
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: Math.max(44, Math.ceil(44 * rt.fontScale)),
        height: (shouldGiveExtraPadding || hasChips) ? 'auto' : Math.max(44, Math.ceil(44 * rt.fontScale)),
        backgroundColor: isNotOnSurface ? colors.surface : colors.inputBackground,
        boxShadow: isNotOnSurface ? shadows.card : shadows.none,
        paddingLeft: 14,
        paddingVertical: shouldGiveExtraPadding ? 10 : (hasChips ? 5 : 0),
        borderRadius: roundness.sm,
        overflow: "hidden",
        _web: {
            transition: "all 0.15s ease",
            _focus: {
                boxShadow: disabled ? "" : shadows.input,
            },
            _hover: {
                backgroundColor: disabled ? (isNotOnSurface ? colors.surface : colors.inputBackground) : colors.hover,
            }
        }
    }),
    sub_header: {
        marginTop: 5,
        fontSize: typography.caption,
        textAlign: "left",
        color: colors.textSecondary,
    },
    body: {
        marginTop: 5,
        fontSize: 11,
        textAlign: "left",
        fontStyle: "italic",
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    card: (isSelected, disabled) => ({
        marginVertical: 5,
        opacity: disabled ? 0.5 : 1,
        _web: {
            transition: "background-color 0.15s ease",
            userSelect: "none",
            _hover: {
                backgroundColor: colors.hoverCard,
            }
        }
    }),
    selectArea: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        _web: {
            cursor: "pointer",
        }
    },
    labelContainer: {
        paddingBottom: 6,
        flexDirection: "row",
        alignItems: "center",
        marginTop: spacing.sm
    },
    labelText: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
        flexShrink: 1,
    },
    required: {
        color: colors.error,
        fontSize: 14,
        fontFamily: fonts.body,
    },
    errorText: {
        fontSize: typography.body,
        fontStyle: "italic",
        marginTop: 5,
        color: colors.error,
    },
    cardContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 15,
    },
    inputContent: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        height: "100%",
    },
    placeholderText: (isWeb) => ({
        marginTop: isWeb ? -2 : 0,
    }),
    placeholder: {
        color: colors.textSecondary,
        fontSize: typography.body,
        fontFamily: fonts.body,
    },
    // Chips area inside the trigger: plain wrapping row, no height limit.
    // Overflow is handled by the "+N more" chip (MAX_VISIBLE_CHIPS), not scrolling.
    chipsWrap: {
        flex: 1,
        flexShrink: 1,
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 4,
        paddingRight: 4,
    },
    // Bottom margin lives here, not on the trigger's container: on web the
    // DropdownMenu trigger wraps the container and flex layout counts child
    // margins in the parent's height, so a container margin made the focus ring
    // hang below the input. children-based triggers never had the margin.
    inputWrapper: (hasDefaultTrigger) => ({
        position: "relative",
        marginBottom: hasDefaultTrigger ? spacing.sm : 0,
    }),
    sheetHeader: {
        paddingBottom: spacing.sm,
    },
    searchRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
    },
    // Visible boundary around the sheet search input: the SearchBox inside a
    // surface-colored sheet had no delineation, so a subtle bordered wrap
    // (with a transparent input via `nobackground`) marks the region. Shared
    // by the web picker panel's search row (searchBoxWrap).
    searchBoxContainer: {
        flex: 1,
        borderWidth: 1,
        borderColor: colors.borderSubtle,
        borderRadius: roundness.sm,
        overflow: "hidden",
    },
    searchBoxWrap: {
        flex: 1,
        borderWidth: 1,
        borderColor: colors.borderSubtle,
        borderRadius: roundness.sm,
        overflow: "hidden",
    },
    headerActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    actionsRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 10,
        paddingHorizontal: 2,
    },
    subsetRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginTop: 10,
        paddingVertical: 6,
        paddingHorizontal: 2,
    },
    subsetRowText: {
        flex: 1,
        fontSize: typography.caption,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    subsetRowAction: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.tasks,
    },
    selectionCount: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    listContainer: (isWeb) => ({
        ...(isWeb ? { flex: 1 } : { height: rt.screen.height - 220 }),
    }),
    listFooter: {
        paddingBottom: 20,
    },
    centered: {
        textAlign: 'center',
        color: colors.textSecondary,
    },
    loaderContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    // ─── Folder hierarchy rows ──────────────────────────────────────────────
    // Compact indented rows (Move Folder modal pattern): paddingLeft scales
    // with depth, chevron pressable and leaf spacer are both 26px effective
    // width (18 icon + 4 padding + 4 margin = 26) so names align.
    treeRow: (depth, modalMode) => ({
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 6,
        paddingRight: modalMode ? 0 : 12,
        paddingLeft: (modalMode ? 0 : 16) + depth * 20,
        _web: {
            cursor: "pointer",
            transition: "background-color 0.15s ease",
            // Slight drags while clicking were selecting the row text instead
            // of firing onPress — block selection so one click always works.
            userSelect: "none",
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    }),
    treeRowSelected: {
        backgroundColor: colors.primaryContainer,
        // Keep the selected tint under the pointer — later in the style array
        // so it wins over the generic treeRow hover.
        _web: {
            _hover: {
                backgroundColor: colors.primaryContainer,
            },
        },
    },
    treeChevron: {
        marginRight: 4,
        padding: 2,
        alignItems: "center",
        justifyContent: "center",
        _web: {
            cursor: "pointer",
        },
    },
    treeSpacer: {
        width: 26,
    },
    treeRowText: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
        flexShrink: 1,
    },
    treeRowTextSelected: {
        color: colors.primary,
        fontFamily: fonts.bold,
    },
    headingRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        flexShrink: 1,
    },
    countsRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        marginTop: 6,
    },
    countPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    countPillText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    countPillTextInfo: {
        fontSize: typography.caption,
        fontFamily: fonts.heading,
        color: colors.info,
    },
    selectedBadge: {
        backgroundColor: colors.primaryContainer,
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
    selectedBadgeText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    // ─── Web folder-picker panel (dropdownComponent) ────────────────────────
    pickerContent: {
        flexShrink: 1,
    },
    pickerToolbar: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 10,
        paddingVertical: 8,
        gap: 8,
    },
    pickerToolbarTitle: {
        flex: 1,
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    pickerToolbarActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
    },
    pickerToolbarButton: {
        width: 30,
        height: 30,
        borderRadius: 6,
        alignItems: "center",
        justifyContent: "center",
        _web: {
            cursor: "pointer",
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    pickerToolbarSep: {
        width: 1,
        height: 18,
        backgroundColor: colors.borderSubtle,
        marginHorizontal: 4,
    },
    pickerSearchRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingHorizontal: 10,
        paddingBottom: 6,
    },
    pickerList: {
        maxHeight: rt.screen.height / 2.2,
        flexGrow: 0,
    },
    pickerListContent: {
        paddingBottom: 8,
    },
    pickerFooter: {
        textAlign: "center",
        color: colors.textSecondary,
        fontSize: typography.caption,
        paddingVertical: 8,
    },
}));

export default React.memo(SheetSelector)